// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';

// material ui
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';

// shared components
import { ConfirmationDialog } from '@/components/ui/dialog/ConfirmationDialog';

// feature-components
import EnergyXplorerAdvancedAlarmGroupsDialog from '@/features/energyxplorer/components/alarms/EnergyXplorerAdvancedAlarmGroupsDialog';

type EnergyXplorerAlarmManagementActionsProps = {
  accountId: string | undefined;
  autoCreatedAlarmGroupName?: string | null;
  canDeleteAutoCreatedAlarmGroup?: boolean;
  isDeletingAutoCreatedAlarmGroup?: boolean;
  onDeleteAutoCreatedAlarmGroup?: () => Promise<boolean>;
};

export default function EnergyXplorerAlarmManagementActions({
  accountId,
  autoCreatedAlarmGroupName,
  canDeleteAutoCreatedAlarmGroup = false,
  isDeletingAutoCreatedAlarmGroup = false,
  onDeleteAutoCreatedAlarmGroup,
}: EnergyXplorerAlarmManagementActionsProps) {
  const { t } = useTranslation(['energy-analysis', 'common']);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const isMenuOpen = Boolean(menuAnchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenAdvanced = () => {
    handleCloseMenu();
    setIsAdvancedOpen(true);
  };

  const handleOpenDeleteConfirm = () => {
    handleCloseMenu();
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!onDeleteAutoCreatedAlarmGroup) {
      return;
    }

    const wasDeleted = await onDeleteAutoCreatedAlarmGroup();

    if (wasDeleted) {
      setIsDeleteConfirmOpen(false);
    }
  };

  return (
    <>
      {accountId || canDeleteAutoCreatedAlarmGroup ? (
        <IconButton
          size="small"
          onClick={handleOpenMenu}
          aria-controls={isMenuOpen ? 'alarm-management-menu' : undefined}
          aria-expanded={isMenuOpen ? 'true' : undefined}
          aria-haspopup="menu"
          aria-label={t('alarm_setup.advanced_action', {
            ns: 'energy-analysis',
          })}
        >
          <MoreVertIcon />
        </IconButton>
      ) : null}

      <Menu
        id="alarm-management-menu"
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {accountId ? (
          <MenuItem onClick={handleOpenAdvanced}>
            <ListItemIcon>
              <TuneRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {t('alarm_setup.advanced_action', { ns: 'energy-analysis' })}
            </ListItemText>
          </MenuItem>
        ) : null}

        {canDeleteAutoCreatedAlarmGroup ? (
          <MenuItem
            onClick={handleOpenDeleteConfirm}
            disabled={isDeletingAutoCreatedAlarmGroup}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {t('alarm_setup.delete_action', {
                ns: 'energy-analysis',
                defaultValue: 'Delete quick setup',
              })}
            </ListItemText>
          </MenuItem>
        ) : null}
      </Menu>

      <EnergyXplorerAdvancedAlarmGroupsDialog
        open={isAdvancedOpen}
        onClose={() => setIsAdvancedOpen(false)}
        accountId={accountId}
      />

      <ConfirmationDialog
        open={isDeleteConfirmOpen}
        variant="delete"
        title={t('alarm_setup.delete_title', {
          ns: 'energy-analysis',
          defaultValue: 'Delete EnergyXplorer alarm setup',
        })}
        message={
          <Typography variant="body2">
            {t('alarm_setup.delete_description', {
              ns: 'energy-analysis',
              defaultValue:
                'This removes the automatically created alarm group {{groupName}} for your user.',
              groupName: autoCreatedAlarmGroupName
                ? `"${autoCreatedAlarmGroupName}"`
                : '',
            })}
          </Typography>
        }
        confirmLabel={t('alarm_setup.delete_action', {
          ns: 'energy-analysis',
          defaultValue: 'Delete quick setup',
        })}
        loading={isDeletingAutoCreatedAlarmGroup}
        onConfirm={handleConfirmDelete}
        onClose={() => setIsDeleteConfirmOpen(false)}
      />
    </>
  );
}
